import { useEffect, useState } from 'react';
import { Modal, Button, Input, Select } from '@react-apps/design-system';
import type {
  Invoice,
  PaymentMethod,
  TransactionDetail,
} from '../../domain/entities/sales.entity';
import {
  useInvalidateManualTransaction,
  useManuallySettleInvoice,
} from '../hooks/use-sales-actions';
import {
  ErrorBanner,
  InfoNotice,
  InvoiceStatusBadge,
  PAYMENT_METHOD_OPTIONS,
  SuccessBanner,
  formatCurrency,
  getApiErrorMessage,
} from '../shared/display';
import { JustificationFields, useJustification } from './justification-fields';

/** `datetime-local` → ISO (o backend valida `IsDateString`). */
function localInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

// --- Baixa manual de fatura ---

/**
 * Quita uma fatura à mão — pagamento que entrou fora do gateway (PIX na conta,
 * boleto pago em outro lugar) e nunca vai virar webhook. Produz uma transação
 * `manual`/`paid` que segue o mesmo caminho do pagamento aprovado, então pode
 * ativar o contrato e liberar entrega: por isso a justificativa é obrigatória.
 */
export function ManualSettlementModal({
  invalidateKey,
  invoice,
  open,
  onClose,
}: {
  invalidateKey: readonly unknown[];
  invoice: Invoice;
  open: boolean;
  onClose: () => void;
}) {
  const justification = useJustification();
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const mutation = useManuallySettleInvoice(invalidateKey);

  useEffect(() => {
    if (open) {
      justification.reset();
      setAmount('');
      setPaidAt('');
      setPaymentMethod('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const done = mutation.isSuccess;
  // Fatura sem valor definido (ciclo futuro criado vazio): o valor recebido é a
  // única fonte do montante, então deixa de ser opcional.
  const amountRequired = invoice.value == null;
  const parsedAmount = amount.trim() === '' ? null : Number(amount);
  const amountInvalid =
    parsedAmount !== null && (Number.isNaN(parsedAmount) || parsedAmount <= 0);
  const blocked =
    justification.isIncomplete ||
    amountInvalid ||
    (amountRequired && parsedAmount === null);

  const handleConfirm = async () => {
    const body = await justification.buildBody();
    if (!body) return;
    mutation.mutate({
      invoiceId: invoice.id,
      body: {
        ...body,
        amount: parsedAmount ?? undefined,
        paidAt: localInputToIso(paidAt),
        paymentMethod: (paymentMethod || undefined) as
          | PaymentMethod
          | undefined,
      },
    });
  };

  const busy = mutation.isPending || justification.isUploading;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Baixa manual da fatura"
      description="Registra um pagamento que aconteceu fora do gateway e nunca chegará por webhook."
      size="md"
      footer={
        done ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button onClick={handleConfirm} disabled={busy || blocked}>
              {justification.isUploading
                ? 'Enviando anexo…'
                : mutation.isPending
                  ? 'Dando baixa…'
                  : 'Confirmar baixa'}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {done && (
          <SuccessBanner message="Fatura quitada. A transação manual entrou no histórico e o contrato foi recomputado." />
        )}
        {mutation.isError && (
          <ErrorBanner
            message={getApiErrorMessage(
              mutation.error,
              'Falha ao dar baixa na fatura.',
            )}
          />
        )}
        {!done && (
          <>
            <InfoNotice>
              Segue o mesmo caminho do pagamento aprovado por webhook — pode
              ativar o contrato e liberar entrega. Se o gateway confirmar o
              mesmo ciclo depois, o pagamento é registrado como{' '}
              <strong>duplicidade</strong> e vira um alerta na Central de
              Pendências para o time endereçar.
            </InfoNotice>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-(--color-text-secondary)">
                Ciclo {invoice.cycleIndex} ·
              </span>
              <InvoiceStatusBadge status={invoice.status} />
              <span className="text-(--color-text-primary)">
                {invoice.value == null
                  ? 'sem valor definido'
                  : formatCurrency(invoice.value)}
              </span>
            </div>
            <JustificationFields
              state={justification}
              placeholder="Ex.: PIX recebido na conta da Primum em 12/08, comprovante em anexo"
              disabled={busy}
            />
            <Input
              label={
                amountRequired
                  ? 'Valor recebido (obrigatório)'
                  : 'Valor recebido (opcional)'
              }
              id="manual-settlement-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={
                invoice.value == null
                  ? 'Ex.: 199.90'
                  : `Padrão: ${invoice.value}`
              }
              error={amountInvalid ? 'Informe um valor positivo.' : undefined}
              helperText={
                amountRequired
                  ? 'Esta fatura não tem valor definido — informe quanto entrou.'
                  : 'Em branco usa o valor da própria fatura.'
              }
            />
            <Input
              label="Quando o pagamento aconteceu (opcional)"
              id="manual-settlement-paid-at"
              type="datetime-local"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              helperText="Em branco registra agora."
            />
            <Select
              label="Forma de pagamento (opcional)"
              id="manual-settlement-payment-method"
              placeholder="Não informar"
              options={PAYMENT_METHOD_OPTIONS}
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

// --- Anular lançamento manual ---

/**
 * Anula um lançamento **manual** equivocado (`voided`). Só aparece para
 * transações de origem manual: o que veio do gateway é espelho de um fato que
 * vive lá e se desfaz lá (estorno real volta por webhook).
 *
 * O status da fatura é **derivado** das transações que sobram — se a do gateway
 * ainda sustenta o ciclo, a fatura segue paga; se era a única, volta a
 * pendente e o contrato é recomputado.
 */
export function InvalidateManualTransactionModal({
  invalidateKey,
  transaction,
  open,
  onClose,
}: {
  invalidateKey: readonly unknown[];
  transaction: TransactionDetail;
  open: boolean;
  onClose: () => void;
}) {
  const justification = useJustification();
  const mutation = useInvalidateManualTransaction(invalidateKey);

  useEffect(() => {
    if (open) {
      justification.reset();
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const done = mutation.isSuccess;
  const result = mutation.data;
  const busy = mutation.isPending || justification.isUploading;

  const handleConfirm = async () => {
    const body = await justification.buildBody();
    if (!body) return;
    mutation.mutate({ transactionId: transaction.id, body });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Anular lançamento manual"
      description="Desfaz uma baixa manual lançada por engano. O valor não volta para ninguém — só o registro é anulado."
      size="md"
      footer={
        done ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              disabled={busy || justification.isIncomplete}
            >
              {justification.isUploading
                ? 'Enviando anexo…'
                : mutation.isPending
                  ? 'Anulando…'
                  : 'Confirmar anulação'}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {done && (
          <SuccessBanner
            message={
              result?.invoiceRevertedTo
                ? `Lançamento anulado. A fatura voltou para "${result.invoiceRevertedTo}"${
                    result.contractRevertedTo
                      ? ` e o contrato para "${result.contractRevertedTo}"`
                      : ''
                  }.`
                : 'Lançamento anulado. A fatura segue paga — outra transação ainda sustenta o ciclo.'
            }
          />
        )}
        {mutation.isError && (
          <ErrorBanner
            message={getApiErrorMessage(
              mutation.error,
              'Falha ao anular o lançamento.',
            )}
          />
        )}
        {!done && (
          <>
            <InfoNotice>
              A fatura é recalculada pelas transações que sobrarem: se outra
              pagou o mesmo ciclo, ela <strong>segue paga</strong>; se esta era
              a única, volta a <strong>pendente</strong> e o contrato é
              recomputado. Depois de anular, é possível lançar outra baixa
              manual na mesma fatura.
            </InfoNotice>
            <JustificationFields
              state={justification}
              placeholder="Ex.: baixa lançada na fatura errada — o pagamento era do ciclo 3"
              disabled={busy}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
